const experiences = [
  {
    icon: "ri-bubble-chart-line",
    tag: "For Certified Divers",
    heading: "Certified Dive Tour",
    badge: "Best Seller",
    price: "$799",
    priceNote: "per person · 2 reef sites",
    description:
      "Dive Beaver Reef and the outer Great Barrier Reef aboard MV Mahi-Mahi with PADI-certified dive guides who know every coral head and current. Suitable for Open Water certified divers and above.",
    highlight: "Open Water certified & above",
    included: [
      "2 reef site dives",
      "All dive equipment & wetsuit",
      "PADI dive guide",
      "Reef tax / Marine Park fee",
      "Lunch & refreshments",
      "Water taxi transfer",
    ],
    img: "https://public.readdy.ai/ai/img_res/9bf524176007ea226bfc0005a932d605.jpg",
  },
  {
    icon: "ri-water-flash-line",
    tag: "No Experience Needed",
    heading: "Discover Scuba Diving",
    price: "$699",
    priceNote: "per person · no cert needed",
    description:
      "Never dived before? Our PADI Discover Scuba Diving program gets you underwater safely with zero prior experience required. A PADI instructor is with you every fin-kick of the way.",
    highlight: "No experience required",
    included: [
      "All dive equipment & wetsuit",
      "PADI instructor (1-on-1)",
      "Reef tax / Marine Park fee",
      "Lunch & refreshments",
      "Water taxi transfer",
    ],
    img: "https://readdy.ai/api/search-image?query=beginner%20scuba%20diver%20with%20instructor%20underwater%20on%20a%20shallow%20coral%20reef%20in%20Queensland%20Australia%2C%20calm%20clear%20turquoise%20water%2C%20colorful%20tropical%20fish%2C%20bright%20coral%20gardens%2C%20sunlight%20beams%20through%20water%2C%20warm%20encouraging%20atmosphere%2C%20first%20time%20diving%20experience%2C%20beautiful%20tropical%20reef&width=400&height=280&seq=bedarra-exp-02&orientation=landscape",
  },
  {
    icon: "ri-sun-line",
    tag: "For Everyone",
    heading: "All Day Snorkel Tour",
    price: "$449",
    priceNote: "per person · full day",
    description:
      "Join us aboard MV Mahi-Mahi for a guided snorkel experience floating above the coral gardens of the Great Barrier Reef. Turtles, reef sharks, giant clams — the outer reef delivers.",
    highlight: "Suitable for all ages",
    included: [
      "Full day on the reef",
      "Snorkel gear (mask, fins, wetsuit)",
      "Guided snorkel session",
      "Reef tax / Marine Park fee",
      "Lunch & refreshments",
      "Water taxi transfer",
      "Underwater photos",
    ],
    img: "https://readdy.ai/api/search-image?query=snorkeler%20floating%20above%20vibrant%20Great%20Barrier%20Reef%20coral%20gardens%20Queensland%20Australia%2C%20aerial%20underwater%20view%2C%20tropical%20fish%20and%20sea%20turtles%20visible%20below%20surface%2C%20crystal%20clear%20turquoise%20water%2C%20bright%20sunny%20tropical%20day%2C%20stunning%20reef%20colors%2C%20shallow%20reef%20snorkeling%20experience&width=400&height=280&seq=bedarra-exp-03&orientation=landscape",
  },
];

export default function Experience() {
  return (
    <section id="experience" className="w-full bg-white py-24 px-6 md:px-10">
      <div className="max-w-6xl mx-auto">

        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[3px] text-cyan-500 font-semibold mb-4">
            What&apos;s On Offer
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight mb-5">
            Your Reef. Your Day.
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto leading-relaxed font-light">
            Great Barrier Reef diving from Mission Beach suits every level — seasoned diver, total first-timer, or just want to float above the coral. There&apos;s a place on MV Mahi-Mahi for all of you.
          </p>
        </div>

        {/* Experience Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          {experiences.map((exp) => (
            <div
              key={exp.heading}
              className={`group flex flex-col rounded-2xl overflow-hidden border transition-all duration-300 hover:-translate-y-1 ${'badge' in exp && exp.badge ? 'border-orange-200 hover:border-orange-300' : 'border-gray-100 hover:border-cyan-200'}`}
            >
              {/* Image */}
              <div className="relative w-full h-52 overflow-hidden">
                <img
                  src={exp.img}
                  alt={exp.heading}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  width="400"
                  height="280"
                  sizes="(max-width: 768px) 100vw, 400px"
                  loading="lazy"
                  decoding="async"
                />
                {'badge' in exp && exp.badge && (
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                    <i className="ri-award-fill text-sm" />
                    {exp.badge}
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex flex-col flex-1 p-7 bg-white">

                {/* Icon + Tag */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-cyan-50 text-cyan-500">
                    <i className={`${exp.icon} text-xl`} />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-widest text-cyan-500">
                    {exp.tag}
                  </span>
                </div>

                {/* Heading */}
                <h3 className="text-xl font-bold text-gray-900 mb-3 leading-snug">
                  {exp.heading}
                </h3>

                {/* Description */}
                <p className="text-gray-500 text-sm leading-relaxed">
                  {exp.description}
                </p>

                {/* What's Included */}
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
                    What&apos;s Included
                  </p>
                  <ul className="space-y-2">
                    {exp.included.map((item) => (
                      <li key={item} className="flex items-center gap-2.5 text-sm text-gray-600">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-emerald-500">
                          <i className="ri-check-line text-base" />
                        </div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Price */}
                <div className="mt-5 pt-5 border-t border-gray-100 flex items-baseline gap-2">
                  <span className="text-3xl font-black text-gray-900">{exp.price}</span>
                  <span className="text-xs text-gray-400 font-medium">{exp.priceNote}</span>
                </div>

                {/* Highlight Badge */}
                <div className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full w-fit">
                  <i className="ri-checkbox-circle-line w-4 h-4 flex items-center justify-center" />
                  {exp.highlight}
                </div>

              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA nudge */}
        <div className="mt-16 text-center">
          <p className="text-gray-400 text-sm mb-5 tracking-wide">
            All experiences depart from Clump Point Jetty, Mission Beach — a short transfer from Bedarra Island.
          </p>
          <a
            href="https://wa.me/61455957263?text=Hi!%20I'd%20like%20to%20book%20a%20dive%20trip%20to%20Bedarra%20Island."
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-sm cursor-pointer hover:scale-105 transition-transform duration-300"
            style={{ boxShadow: "0 8px 28px rgba(249,115,22,0.35)" }}
          >
            <i className="ri-calendar-check-line w-5 h-5 flex items-center justify-center" />
            Book Your Dive Day Trip
          </a>
        </div>

      </div>
    </section>
  );
}
